import React, { useCallback, useMemo, useState } from 'react';
import {
  ActionIcon, Button, Checkbox, Loader, NumberInput, Radio, Select, Switch, Textarea, Tooltip,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

/**
 * Les contrôles Mantine du configurateur de tableau, dans le contrat attendu
 * par `src/shared/tables/ui/TableConfigBody`.
 *
 * L'écran est le même que celui du Proposal — c'est le composant partagé qui
 * pose la disposition. Seuls les contrôles diffèrent, pour que l'éditeur web
 * n'embarque pas Ant Design.
 */

/* Les icônes du configurateur Ant Design (`tableConfigAntdWidgets`). */
const ICONS = {
  reset: icon({ name: 'rotate-left', style: 'solid' }),
  up: icon({ name: 'chevron-up', style: 'solid' }),
  down: icon({ name: 'chevron-down', style: 'solid' }),
  'align-start': icon({ name: 'align-left', style: 'solid' }),
  'align-center': icon({ name: 'align-center', style: 'solid' }),
  'align-end': icon({ name: 'align-right', style: 'solid' }),
  merge: icon({ name: 'object-group', style: 'solid' }),
  unmerge: icon({ name: 'object-ungroup', style: 'solid' }),
};

/**
 * Mantine ne manipule que des chaînes dans ses selects ; les axes donnée, eux,
 * portent parfois des booléens. On passe par la chaîne à l'aller et on rend la
 * valeur d'origine au retour.
 */
const useStringOptions = (options, value) => useMemo(() => ({
  data: options.map((o) => ({ value: String(o.value), label: o.label, disabled: o.disabled })),
  current: String(value ?? ''),
  back: (str) => options.find((o) => String(o.value) === str)?.value ?? str,
}), [options, value]);

export const tableConfigMantineWidgets = {
  Grip: () => <FontAwesomeIcon icon={icon({ name: 'grip-vertical', style: 'solid' })} style={{ fontSize: 16 }} />,

  Spinner: () => <Loader size="sm" />,

  Checkbox: ({ checked, onChange, ariaLabel }) => (
    <Checkbox checked={checked} onChange={onChange} aria-label={ariaLabel} />
  ),

  TextArea: ({ value, onChange, ariaLabel }) => (
    <Textarea
      size="xs"
      autosize
      minRows={1}
      maxRows={3}
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  ),

  NumberInput: ({ value, min, max, disabled, title, ariaLabel, onChange }) => (
    <Tooltip label={title} disabled={!title} withinPortal>
      <NumberInput
        size="xs"
        w={92}
        min={min}
        max={max}
        suffix=" %"
        disabled={disabled}
        value={value ?? ''}
        aria-label={ariaLabel}
        onChange={(v) => onChange(Number(v) || 1)}
      />
    </Tooltip>
  ),

  Radio: ({ value, options, onChange, ariaLabel }) => {
    const { data, current, back } = useStringOptions(options, value);
    return (
      <Radio.Group value={current} onChange={(v) => onChange(back(v))} aria-label={ariaLabel}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {data.map((o) => (
            <Radio key={o.value} value={o.value} label={o.label} disabled={o.disabled} size="sm" />
          ))}
        </div>
      </Radio.Group>
    );
  },

  Select: ({ value, options, onChange, ariaLabel }) => {
    const { data, current, back } = useStringOptions(options, value);
    return (
      <Select
        size="sm"
        data={data}
        value={current}
        onChange={(v) => onChange(back(v))}
        aria-label={ariaLabel}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
      />
    );
  },

  Switch: ({ checked, disabled, onChange, ariaLabel }) => (
    <Switch
      size="sm"
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  ),

  IconButton: ({ name, title, disabled, ariaLabel, onClick }) => {
    const definition = ICONS[name] || ICONS.reset;
    return (
      <Tooltip label={title} disabled={!title} withinPortal>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={onClick}
        >
          <FontAwesomeIcon icon={definition} style={{ fontSize: 14 }} />
        </ActionIcon>
      </Tooltip>
    );
  },

  TextButton: ({ name, title, disabled, onClick, children }) => {
    const definition = ICONS[name] || ICONS.reset;
    return (
      <Tooltip label={title} disabled={!title} withinPortal>
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          disabled={disabled}
          onClick={onClick}
          leftSection={<FontAwesomeIcon icon={definition} style={{ fontSize: 14 }} />}
        >
          {children}
        </Button>
      </Tooltip>
    );
  },
};

/**
 * Réordonnancement par drag, version HTML native : l'éditeur web n'a pas de
 * `DndProvider` react-dnd, et Puck occupe déjà dnd-kit. La poignée tire, la
 * ligne survolée reçoit — même geste que dans le Proposal.
 *
 * Le déplacement n'est appliqué qu'au dépôt : réordonner au survol ferait
 * bouger la liste sous le curseur sans que la souris ne suive.
 */
export function useHtml5DragAdapter(onDrop) {
  const [dragged, setDragged] = useState(null);
  const [over, setOver] = useState(null);

  const end = useCallback(() => {
    if (dragged && over && dragged !== over) onDrop(dragged, over);
    setDragged(null);
    setOver(null);
  }, [dragged, over, onDrop]);

  return useMemo(() => ({
    useColumnDrag(label) {
      return {
        rowProps: {
          onDragOver: (e) => { e.preventDefault(); setOver(label); },
          onDrop: (e) => { e.preventDefault(); end(); },
        },
        handleProps: {
          draggable: true,
          onDragStart: () => setDragged(label),
          onDragEnd: end,
        },
        dragging: dragged === label,
        dropTarget: over === label && dragged !== label,
      };
    },
  }), [dragged, over, end]);
}
